import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Shield, Truck, Heart, Award, Users, Star } from 'lucide-react';

const About = () => {
    const navigate = useNavigate();

    const features = [
        {
            icon: <Award className="w-8 h-8" />,
            title: "Premium Quality",
            description: "Every product is crafted with the finest materials and attention to detail"
        },
        {
            icon: <Shield className="w-8 h-8" />,
            title: "Trusted Craftsmanship",
            description: "Generations of expertise in leather craftsmanship and design"
        },
        {
            icon: <Truck className="w-8 h-8" />,
            title: "Nationwide Delivery",
            description: "Free shipping across Pakistan with secure packaging"
        },
        {
            icon: <Heart className="w-8 h-8" />,
            title: "Customer First",
            description: "Dedicated to providing exceptional service and support"
        }
    ];

    const stats = [
        { number: "5000+", label: "Happy Customers" },
        { number: "100+", label: "Cities Served" },
        { number: "4.9/5", label: "Customer Rating" },
        { number: "24/7", label: "Support" }
    ];

    const handleExploreCollection = () => {
        navigate('/shop');
    };

    const handleContactUs = () => {
        navigate('/contact');
    };

    return (
        <div className="min-h-screen bg-beige">
            {/* Hero Section */}
            <div className="bg-charcoal text-offwhite py-20">
                <div className="max-w-6xl mx-auto px-4 text-center">
                    <h1 className="text-5xl font-bold mb-6">About Leatherhood PK</h1>
                    <p className="text-xl text-tan max-w-3xl mx-auto leading-relaxed">
                        Where tradition meets contemporary style. We are passionate about bringing you 
                        premium leather products that tell a story of craftsmanship, durability, and elegance.
                    </p>
                </div>
            </div>

            {/* Mission Section */}
            <div className="py-16 bg-offwhite">
                <div className="max-w-6xl mx-auto px-4">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
                        <div>
                            <h2 className="text-4xl font-bold text-charcoal mb-6">Our Story</h2>
                            <p className="text-lg text-darktaupe mb-6 leading-relaxed">
                                Founded with a vision to revive Pakistan's rich leather heritage, Leatherhood PK 
                                brings you authentic, high-quality leather goods that stand the test of time. 
                                Each piece is a testament to our commitment to excellence and sustainability.
                            </p>
                            <p className="text-lg text-darktaupe leading-relaxed">
                                We work directly with skilled artisans across Pakistan, ensuring fair trade 
                                practices while preserving traditional craftsmanship techniques that have been 
                                passed down through generations.
                            </p>
                        </div>
                        <div className="bg-tan rounded-lg p-8 text-center">
                            <Users className="w-16 h-16 mx-auto mb-4 text-primary" />
                            <h3 className="text-2xl font-bold text-charcoal mb-4">Our Mission</h3>
                            <p className="text-darktaupe text-lg">
                                To provide exceptional leather products that combine traditional craftsmanship 
                                with modern design, while promoting sustainable practices and supporting local artisans.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Features Section */}
            <div className="py-16 bg-white">
                <div className="max-w-6xl mx-auto px-4">
                    <h2 className="text-4xl font-bold text-charcoal text-center mb-4">Why Choose Leatherhood PK?</h2>
                    <p className="text-xl text-darktaupe text-center mb-12 max-w-3xl mx-auto">
                        We take pride in every detail, ensuring you receive nothing but the best
                    </p>
                    
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
                        {features.map((feature, index) => (
                            <div key={index} className="text-center p-6 rounded-lg hover:shadow-lg transition-shadow duration-300">
                                <div className="bg-primary rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4 text-offwhite">
                                    {feature.icon}
                                </div>
                                <h3 className="text-xl font-bold text-charcoal mb-3">{feature.title}</h3>
                                <p className="text-darktaupe">{feature.description}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Stats Section */}
            <div className="py-16 bg-primary text-offwhite">
                <div className="max-w-6xl mx-auto px-4">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
                        {stats.map((stat, index) => (
                            <div key={index}>
                                <div className="text-4xl font-bold mb-2">{stat.number}</div>
                                <div className="text-tan text-lg">{stat.label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* Values Section */}
            <div className="py-16 bg-beige">
                <div className="max-w-6xl mx-auto px-4">
                    <h2 className="text-4xl font-bold text-charcoal text-center mb-12">Our Values</h2>
                    
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        <div className="bg-white p-8 rounded-lg shadow-sm">
                            <div className="w-12 h-12 bg-deepgreen rounded-full flex items-center justify-center mb-4">
                                <Heart className="w-6 h-6 text-offwhite" />
                            </div>
                            <h3 className="text-xl font-bold text-charcoal mb-3">Quality First</h3>
                            <p className="text-darktaupe">
                                We never compromise on quality. Every stitch, every cut, and every finish 
                                is meticulously checked to ensure perfection.
                            </p>
                        </div>
                        
                        <div className="bg-white p-8 rounded-lg shadow-sm">
                            <div className="w-12 h-12 bg-olive rounded-full flex items-center justify-center mb-4">
                                <Users className="w-6 h-6 text-offwhite" />
                            </div>
                            <h3 className="text-xl font-bold text-charcoal mb-3">Artisan Support</h3>
                            <p className="text-darktaupe">
                                We believe in fair trade and work directly with artisans, ensuring they 
                                receive fair compensation for their exceptional skills.
                            </p>
                        </div>
                        
                        <div className="bg-white p-8 rounded-lg shadow-sm">
                            <div className="w-12 h-12 bg-redburnt rounded-full flex items-center justify-center mb-4">
                                <Star className="w-6 h-6 text-offwhite" />
                            </div>
                            <h3 className="text-xl font-bold text-charcoal mb-3">Sustainability</h3>
                            <p className="text-darktaupe">
                                We are committed to sustainable practices, using ethically sourced materials 
                                and environmentally conscious production methods.
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            {/* CTA Section */}
            <div className="py-16 bg-taupe text-offwhite">
                <div className="max-w-4xl mx-auto px-4 text-center">
                    <h2 className="text-4xl font-bold mb-6">Join Our Leather Community</h2>
                    <p className="text-xl mb-8 max-w-2xl mx-auto">
                        Experience the perfect blend of tradition and modernity. Discover why thousands of 
                        customers trust Leatherhood PK for their leather needs.
                    </p>
                    <div className="flex flex-col sm:flex-row gap-4 justify-center">
                        <button 
                            onClick={handleExploreCollection}
                            className="bg-primary text-offwhite px-8 py-3 rounded-lg hover:bg-charcoal transition-colors font-semibold"
                        >
                            Explore Collection
                        </button>
                        <button 
                            onClick={handleContactUs}
                            className="border-2 border-offwhite text-offwhite px-8 py-3 rounded-lg hover:bg-offwhite hover:text-charcoal transition-colors font-semibold"
                        >
                            Contact Us
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default About;